import { z } from 'zod';

import { toString } from '../../utils/common.util';
import { extractByPath } from '../../utils/json.util';
import * as Blocks from '../blocks';
import { InvalidComponentError } from './base.component';

export const ComponentLiteral =
  Blocks.PropertyBlock.ComponentPropertyBlock.CustomTextInputLiteral;

export const TextInputComponentSchema = z.object({
  name: Blocks.PropertyBlock.NamePropertyBlock.NameBlockSchema,
  component: ComponentLiteral,
  params: z.object({
    validations: z
      .object({
        maxLength:
          Blocks.ValidationBlock.MaxLengthValidation.MaxLengthValidationBlockSchema.optional(),
        required:
          Blocks.ValidationBlock.RequiredValidation.RequireBlockSchema.optional()
      })
      .optional(),
    labels: Blocks.PropertyBlock.LabelPropertyBlock.LabelBlockSchema.optional(),
    caseDataField:
      Blocks.PropertyBlock.CaseDataPropertyBlock.CaseDataFieldSchema,
    actions: z.array(
      Blocks.PropertyBlock.ActionPropertyBlock.ActionPropertyBlockSchema
    )
  })
});
export type TextInputComponent = z.infer<typeof TextInputComponentSchema>;

export const TextInputComponentWithValueSchema = TextInputComponentSchema.merge(
  z.object({
    value: z.string().optional()
  })
);
export type TextInputComponentWithValue = z.infer<
  typeof TextInputComponentWithValueSchema
>;

type isValidStructure = (structure: unknown) => structure is TextInputComponent;
export const isValidStructure: isValidStructure = (
  structure: unknown
): structure is TextInputComponent =>
  TextInputComponentSchema.safeParse(structure).success;

type IsValidateData = (
  component: TextInputComponent,
  value: unknown
) => boolean;
export const isValidateData: IsValidateData = (
  component: TextInputComponent,
  value
): boolean => {
  if (!component.params.validations) {
    return true;
  }

  const { maxLength, required } = component.params.validations;
  if (required !== undefined) {
    if (
      Blocks.ValidationBlock.RequiredValidation.isRequired(value, required) ===
      false
    ) {
      return false;
    }
  }

  if (maxLength !== undefined) {
    if (
      Blocks.ValidationBlock.MaxLengthValidation.isMaxLength(
        value,
        maxLength
      ) === false
    ) {
      return false;
    }
  }

  return true;
};

type PopulateFromCaseDataField = (
  field: TextInputComponent,
  inputJSON: Record<string, unknown>
) => TextInputComponentWithValue;
export const populateFromCaseDataField: PopulateFromCaseDataField = (
  field,
  inputJSON
) => {
  return {
    ...field,
    value: toString(
      extractByPath({ json: inputJSON, path: field.params.caseDataField })
    )
  };
};

export type BuildParams = Omit<TextInputComponent, 'component'>;
type Build = (
  params: BuildParams
) => TextInputComponent | InvalidComponentError;
export const build: Build = params => {
  const component = {
    ...params,
    component: ComponentLiteral.value
  };

  const parsedResult = TextInputComponentSchema.safeParse(component);
  if (parsedResult.success) {
    return parsedResult.data;
  }

  return new InvalidComponentError(
    ComponentLiteral.value,
    parsedResult.error,
    params
  );
};
